import { ReactNode, useMemo } from 'react';
import { Box, styled } from '@mui/material';

import StatusDot from './StatusDot';
import StatusText from './StatusText';
import { StatusType } from './types';
import StatusDescription from './StatusDescription';
import { AccessStatus } from '@client-app-sdk';

export const CONNECTED_STATUS = 'connected';
export const DISCONNECTED_STATUS = 'disconnected';

const StatusWrapper = styled(Box)(({ theme }) => ({
  display: 'block',
  position: 'relative',
  padding: theme.spacing(0, 0, 0, 2.5),

  '& .status-dot': {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },

  '& .status-info': {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

export function Status({ status, description }: { status: string; description?: ReactNode }) {
  const statusType = useMemo(() => {
    switch (status) {
      case AccessStatus.Active:
      case AccessStatus.GRANTED:
      case CONNECTED_STATUS:
        return StatusType.IsSuccess;
      case DISCONNECTED_STATUS:
        return;
      case AccessStatus.Rejected:
      case AccessStatus.REJECTED:
      case AccessStatus.Failed:
      case AccessStatus.FAILED:
        return StatusType.IsError;
      case AccessStatus.Revoking:
      case AccessStatus.REVOKING:
      case AccessStatus.Granting:
      case AccessStatus.APPROVED:
        return StatusType.IsPending;
      case AccessStatus.Pending:
      case AccessStatus.PENDING:
      case AccessStatus.Revoked:
      case AccessStatus.EXPIRED:
        return StatusType.IsWarning;
    }
  }, [status]);

  const formattedStatus = useMemo(() => {
    switch (status) {
      case AccessStatus.Pending:
        return 'pending approval';
      case AccessStatus.Granting:
        return 'granting';
      default:
        return status.toLowerCase();
    }
  }, [status]);

  return (
    <StatusWrapper>
      <div className="status-dot">
        <StatusDot type={statusType} />
      </div>
      <StatusText>{formattedStatus}</StatusText>
      {description && (
        <div className="status-info">
          <StatusDescription>{description}</StatusDescription>
        </div>
      )}
    </StatusWrapper>
  );
}
